import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, Avatar } from '@mui/material';

export interface ReportCardSubjectSummary {
  subject: string;
  subjectCode?: string;
  quizScoreObtained: number;
  quizScoreTotal: number;
  assignmentScoreObtained: number;
  assignmentScoreTotal: number;
  testScoreObtained?: number;
  testScoreTotal?: number;
  examScoreObtained?: number;
  examScoreTotal?: number;
}

export interface ReportCardDetails {
  student: {
    _id: string;
    name: string;
    email?: string;
    className?: string;
    department?: string;
    registrationId?: string;
  };
  termName: string;
  attendancePercentage: number;
  isEligibleForExam: boolean;
  attendanceSummary: {
    presentDays: number;
    lateDays: number;
    absentDays: number;
    excusedDays: number;
    totalDays: number;
  };
  termSummary: {
    quizScoreObtained: number;
    quizScoreTotal: number;
    assignmentScoreObtained: number;
    assignmentScoreTotal: number;
    testScoreObtained?: number;
    testScoreTotal?: number;
    examScoreObtained?: number;
    examScoreTotal?: number;
  };
  subjectSummaries: ReportCardSubjectSummary[];
  termDurationMonths?: number;
  termStartDate?: string;
  termEndDate?: string;
  marksSubmissionDeadline?: string;
  schoolName?: string;
  schoolLogoUrl?: string;
  cumulativePercentage?: number | null;
}

interface ReportCardViewProps {
  details: ReportCardDetails;
  onPrint?: () => void;
  showPrintButton?: boolean;
}

const getExamTitle = (className?: string) => {
  if (!className) return 'School Examination';
  const normalized = className.trim().toUpperCase();
  if (normalized.startsWith('JS') || normalized.includes('JUNIOR')) {
    return 'Junior Secondary School';
  }
  if (normalized.startsWith('SS') || normalized.includes('SENIOR')) {
    return 'Senior Secondary School Examination';
  }
  if (/JS\s*\d/.test(normalized)) {
    return 'Junior Secondary School';
  }
  if (/SS\s*\d/.test(normalized)) {
    return 'Senior Secondary School Examination';
  }
  return 'School Examination';
};

const getScorePercentage = (details: ReportCardDetails) => {
  const totalObtained = (details.subjectSummaries || []).reduce((acc, row) => {
    return acc + (row.quizScoreObtained || 0) + (row.assignmentScoreObtained || 0) + (row.testScoreObtained || 0) + (row.examScoreObtained || 0);
  }, 0);
  const totalMax = (details.subjectSummaries || []).length * 100;
  if (totalMax === 0) return 0;
  return parseFloat(((totalObtained / totalMax) * 100).toFixed(2));
};

const isFirstTerm = (termName: string) => /first|1st|term\s*1/i.test(termName);

const ReportCardView: React.FC<ReportCardViewProps> = ({ details, onPrint, showPrintButton = true }) => {
  const title = getExamTitle(details.student.className);
  const termPercentage = getScorePercentage(details);
  const cumulativePercentage = isFirstTerm(details.termName) ? 'N/A' : (details.cumulativePercentage !== undefined && details.cumulativePercentage !== null ? `${details.cumulativePercentage.toFixed(2)}%` : 'N/A');
  const schoolLogo = details.schoolLogoUrl?.trim();
  const printedOn = new Date().toLocaleDateString('en-GB');

  return (
    <Box className="report-card-page" sx={{ py: 4, px: 3, maxWidth: 1000, mx: 'auto', color: '#000' }}>
      <Box sx={{ border: '2px solid #000', p: 3, bgcolor: '#fff', color: '#000' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {schoolLogo ? (
            <Avatar src={schoolLogo} alt={details.schoolName || 'School Logo'} sx={{ width: 96, height: 96, mx: 'auto', mb: 2, border: '1px solid #000' }} />
          ) : (
            <Avatar sx={{ width: 96, height: 96, mx: 'auto', mb: 2, bgcolor: '#0b6623', fontSize: 32 }}>
              {details.schoolName?.charAt(0) ?? 'S'}
            </Avatar>
          )}
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: '#000' }}>
            {details.schoolName || 'School Name'}
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 0.75, fontWeight: 700, color: '#0b6623' }}>
            GRADE CARD
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#333' }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Regd No:</Typography>
              <Typography variant="body1" sx={{ color: '#000' }}>{details.student.registrationId || details.student._id}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Name:</Typography>
              <Typography variant="body1" sx={{ color: '#000' }}>{details.student.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Class:</Typography>
              <Typography variant="body1" sx={{ color: '#000' }}>{details.student.className || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Department:</Typography>
              <Typography variant="body1" sx={{ color: '#000' }}>{details.student.department || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Term:</Typography>
              <Typography variant="body1" sx={{ color: '#000' }}>{details.termName}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ border: '1px solid #0b6623', p: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#333' }}>Attendance %</Typography>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: '#000' }}>{details.attendancePercentage.toFixed(2)}%</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ border: '1px solid #0b6623', p: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#333' }}>Term Percentage</Typography>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: '#000' }}>{termPercentage.toFixed(2)}%</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ border: '1px solid #0b6623', p: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#333' }}>Cumulative Percentage</Typography>
              <Typography variant="h6" sx={{ mt: 1, fontWeight: 700, color: '#000' }}>{cumulativePercentage}</Typography>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ border: '2px solid #0b6623', borderRadius: 1, mb: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                  <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #0b6623', color: '#000' }}>Subject Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #0b6623', color: '#000' }}>
                    Quiz Score (10)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #0b6623', color: '#000' }}>
                    Assignment Score (10)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #0b6623', color: '#000' }}>
                    Test Score (20)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, borderRight: '1px solid #0b6623', color: '#000' }}>
                    Exam Score (60)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#000' }}>Total Score (100)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.subjectSummaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      No subject term records are available for this student.
                    </TableCell>
                  </TableRow>
                ) : (
                  details.subjectSummaries.map((row) => {
                    const subjectTotalObtained =
                      Number(row.quizScoreObtained || 0)
                      + Number(row.assignmentScoreObtained || 0)
                      + Number(row.testScoreObtained || 0)
                      + Number(row.examScoreObtained || 0);

                    return (
                      <TableRow key={row.subject} hover>
                        <TableCell sx={{ borderRight: '1px solid #0b6623', color: '#000' }}>{row.subject}</TableCell>
                        <TableCell sx={{ borderRight: '1px solid #0b6623', color: '#000' }}>{Number(row.quizScoreObtained || 0)}</TableCell>
                        <TableCell sx={{ borderRight: '1px solid #0b6623', color: '#000' }}>{Number(row.assignmentScoreObtained || 0)}</TableCell>
                        <TableCell sx={{ borderRight: '1px solid #0b6623', color: '#000' }}>{Number(row.testScoreObtained || 0)}</TableCell>
                        <TableCell sx={{ borderRight: '1px solid #0b6623', color: '#000' }}>{Number(row.examScoreObtained || 0)}</TableCell>
                        <TableCell sx={{ color: '#000' }}>{subjectTotalObtained}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Printed On:</Typography>
            <Typography variant="body1" sx={{ color: '#000' }}>{printedOn}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Note:</Typography>
            <Typography variant="body1" sx={{ color: '#000' }}>
              This is a digitally generated grade card. For questions, contact your school administrator.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ borderTop: '1px dashed #000', pt: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Exam Coordinator</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ borderTop: '1px dashed #000', pt: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#000' }}>Principal</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportCardView;
